/**
 * Prospects API Client
 */

import { auth } from '../firebase';
import type {
  Prospect,
  ProspectCreate,
  ProspectUpdate,
  ProspectListResponse,
  ProspectListParams,
  ProspectImportResponse,
} from '@/types/prospect';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

/**
 * Get the authorization header with Firebase ID token
 */
async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Get the authorization header for multipart/form-data requests
 */
async function getAuthHeaderMultipart(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    // Don't set Content-Type for multipart - browser will set it with boundary
  };
}

/**
 * List prospects with optional filters and pagination
 */
export async function listProspects(params?: ProspectListParams): Promise<ProspectListResponse> {
  const headers = await getAuthHeader();

  // Build query string
  const queryParams = new URLSearchParams();
  if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
  if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.city) queryParams.append('city', params.city);
  if (params?.state) queryParams.append('state', params.state);
  if (params?.import_batch_id) queryParams.append('import_batch_id', params.import_batch_id);
  if (params?.queue) queryParams.append('queue', params.queue);

  const url = `${API_BASE_URL}/api/prospects${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to list prospects: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a single prospect by ID
 */
export async function getProspect(id: string): Promise<Prospect> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/prospects/${id}`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to get prospect: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new prospect
 */
export async function createProspect(data: ProspectCreate): Promise<Prospect> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/prospects`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to create prospect: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update a prospect
 */
export async function updateProspect(id: string, data: ProspectUpdate): Promise<Prospect> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/prospects/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to update prospect: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a prospect
 */
export async function deleteProspect(id: string): Promise<void> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/prospects/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to delete prospect: ${response.statusText}`);
  }
}

/**
 * Export prospects as CSV (triggers browser download)
 */
export async function exportProspectsCSV(params?: {
  status?: string;
  source?: string;
  format?: 'agency_leads' | 'agency_calls';
}): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const token = await user.getIdToken();
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.source) queryParams.append('source', params.source);
  if (params?.format) queryParams.append('format', params.format);

  const url = `${API_BASE_URL}/api/prospects/export${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to export: ${response.statusText}`);
  }

  // Get filename from Content-Disposition header or generate default
  const disposition = response.headers.get('Content-Disposition');
  const filenameMatch = disposition?.match(/filename=(.+)/);
  const filename = filenameMatch ? filenameMatch[1] : 'prospects_export.csv';

  // Trigger browser download
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Import prospects from CSV files
 */
export async function importProspectsCSV(files: File[]): Promise<ProspectImportResponse> {
  const headers = await getAuthHeaderMultipart();

  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });

  const response = await fetch(`${API_BASE_URL}/api/prospects/import`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to import CSV: ${response.statusText}`);
  }

  return response.json();
}
